import { useState, useEffect } from "react"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card"
import { Button } from "./ui/button"
import { Textarea } from "./ui/textarea"
import { Label } from "./ui/label"
import { Input } from "./ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select"
import { Badge } from "./ui/badge"
import {
  Edit3,
  Save,
  RotateCcw,
  Clock,
  GitBranch,
  Eye,
  EyeOff
} from "lucide-react"

// Helper to render prompt text with highlighted template variables
const HighlightedPrompt = ({ text }: { text: string }) => {
  // Match {{variable}} or {{variable|filter}} patterns
  const parts = text.split(/({{[^}]+}})/g)

  return (
    <>
      {parts.map((part, index) => {
        if (part.match(/^{{[^}]+}}$/)) {
          // This is a template variable - style it distinctly
          return (
            <span
              key={index}
              className="inline-block bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded text-xs font-semibold mx-0.5 border border-blue-200"
            >
              {part}
            </span>
          )
        }
        // Regular text
        return <span key={index}>{part}</span>
      })}
    </>
  )
}

type PromptTemplate = {
  id: string
  name: string
  description?: string
  step: string
  version: number
  systemPrompt: string
  userPrompt: string
  variables?: Record<string, unknown>
  isActive: boolean
  usageCount: number
  successRate?: number
  createdAt: string
  parentId?: string
}

type PromptEditorProps = {
  step: string
  stepName: string
  currentTemplate?: PromptTemplate
  onSave: (template: Partial<PromptTemplate>) => Promise<void>
  onVersionSelect: (templateId: string) => void
  templates: PromptTemplate[]
  isLoading?: boolean
}

export const PromptEditor = ({
  step,
  stepName,
  currentTemplate,
  onSave,
  onVersionSelect,
  templates,
  isLoading = false
}: PromptEditorProps) => {
  const [isEditing, setIsEditing] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [editedTemplate, setEditedTemplate] = useState<Partial<PromptTemplate>>({})

  // Filter templates for this step and sort by version (newest first)
  const stepTemplates = templates
    .filter(t => t.step === step)
    .sort((a, b) => b.version - a.version)

  useEffect(() => {
    if (currentTemplate) {
      setEditedTemplate({
        name: currentTemplate.name,
        description: currentTemplate.description,
        systemPrompt: currentTemplate.systemPrompt,
        userPrompt: currentTemplate.userPrompt,
        variables: currentTemplate.variables
      })
    }
  }, [currentTemplate])

  const handleSave = async () => {
    if (!editedTemplate.systemPrompt || !editedTemplate.userPrompt) {
      return
    }

    try {
      await onSave({
        ...editedTemplate,
        step,
        parentId: currentTemplate?.id
      })
      setIsEditing(false)
    } catch (error) {
      console.error("Failed to save prompt template:", error)
    }
  }

  const handleReset = () => {
    if (currentTemplate) {
      setEditedTemplate({
        name: currentTemplate.name,
        description: currentTemplate.description,
        systemPrompt: currentTemplate.systemPrompt,
        userPrompt: currentTemplate.userPrompt,
        variables: currentTemplate.variables
      })
    }
    setIsEditing(false)
  }

  return (
    <Card className="mb-4">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Edit3 className="h-5 w-5" />
              {stepName} Prompt Template
              {currentTemplate && (
                <Badge variant="secondary">v{currentTemplate.version}</Badge>
              )}
            </CardTitle>
            <CardDescription>
              Customize the AI prompt for this generation step
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}>
              {isExpanded ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              {isExpanded ? "Hide" : "Show"}
            </Button>
            {!isEditing && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEditing(true)}>
                <Edit3 className="h-4 w-4 mr-1" />
                Edit
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent className="space-y-4">
          {/* Template Version History */}
          {stepTemplates.length > 0 && (
            <div className="space-y-2">
              <Label>Template Versions</Label>
              <Select
                value={currentTemplate?.id || ""}
                onValueChange={onVersionSelect}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a template version" />
                </SelectTrigger>
                <SelectContent>
                  {stepTemplates.map((template) => (
                    <SelectItem key={template.id} value={template.id}>
                      <div className="flex items-center gap-2">
                        <span>v{template.version}</span>
                        <span className="text-sm text-muted-foreground">
                          {template.name || "Untitled"}
                        </span>
                        {template.isActive && (
                          <Badge variant="default" className="text-xs">Active</Badge>
                        )}
                        <span className="text-xs text-muted-foreground">
                          Used {template.usageCount} times
                        </span>
                        {template.successRate && (
                          <span className="text-xs text-green-600">
                            {Math.round(template.successRate * 100)}% success
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {isEditing ? (
            <div className="space-y-4">
              {/* Edit Mode */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="template-name">Template Name</Label>
                  <Input
                    id="template-name"
                    placeholder="e.g., Detailed Scenario Generator v2"
                    value={editedTemplate.name || ""}
                    onChange={(e) => setEditedTemplate(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="template-description">Description</Label>
                  <Input
                    id="template-description"
                    placeholder="Brief description of changes..."
                    value={editedTemplate.description || ""}
                    onChange={(e) => setEditedTemplate(prev => ({ ...prev, description: e.target.value }))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="system-prompt">System Prompt</Label>
                <Textarea
                  id="system-prompt"
                  placeholder="System instructions for the AI..."
                  className="min-h-[120px] font-mono text-sm"
                  value={editedTemplate.systemPrompt || ""}
                  onChange={(e) => setEditedTemplate(prev => ({ ...prev, systemPrompt: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="user-prompt">User Prompt Template</Label>
                <Textarea
                  id="user-prompt"
                  placeholder="User prompt with {variables}..."
                  className="min-h-[200px] font-mono text-sm"
                  value={editedTemplate.userPrompt || ""}
                  onChange={(e) => setEditedTemplate(prev => ({ ...prev, userPrompt: e.target.value }))}
                />
                <div className="text-xs text-muted-foreground space-y-1">
                  <p>Use {"{{"} variable_name {"}}"} syntax for dynamic values. Available variables:</p>
                  <div className="bg-gray-50 p-2 rounded border text-xs max-h-24 overflow-y-auto">
                    <div className="grid grid-cols-2 gap-1">
                      <code>{"{{scenario.industry}}"}</code>
                      <code>{"{{scenario.participantRole}}"}</code>
                      <code>{"{{stakeholders|names}}"}</code>
                      <code>{"{{stakeholders|summary}}"}</code>
                      <code>{"{{scenarioDetails.crisis.nature}}"}</code>
                      <code>{"{{messages|count}}"}</code>
                    </div>
                  </div>
                  <p>Use |names, |summary, |count, |list, |json for array transformations</p>
                </div>
              </div>

              <div className="flex gap-2 pt-4">
                <Button onClick={handleSave} disabled={isLoading}>
                  <Save className="h-4 w-4 mr-1" />
                  Save as New Version
                </Button>
                <Button variant="outline" onClick={handleReset}>
                  <RotateCcw className="h-4 w-4 mr-1" />
                  Reset
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* View Mode */}
              {currentTemplate ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      Created {new Date(currentTemplate.createdAt).toLocaleDateString()}
                    </div>
                    <div className="flex items-center gap-1">
                      <GitBranch className="h-4 w-4" />
                      Used {currentTemplate.usageCount} times
                    </div>
                    {currentTemplate.successRate && (
                      <div className="text-green-600">
                        {Math.round(currentTemplate.successRate * 100)}% success rate
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                    <div>
                      <Label className="text-sm font-medium">System Prompt</Label>
                      <div className="mt-1 p-3 bg-gray-50 rounded border font-mono text-sm whitespace-pre-wrap">
                        <HighlightedPrompt text={currentTemplate.systemPrompt} />
                      </div>
                    </div>

                    <div>
                      <Label className="text-sm font-medium">User Prompt Template</Label>
                      <div className="mt-1 p-3 bg-gray-50 rounded border font-mono text-sm whitespace-pre-wrap">
                        <HighlightedPrompt text={currentTemplate.userPrompt} />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Edit3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No prompt template configured for this step</p>
                  <p className="text-sm">Click &quot;Edit&quot; to create your first template</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  )
}